<template>
  <div class="container">
    <section>
      <div class="layout">
        <div class="child">
          <div class="pure-g">
            <div class="pure-u-1">
              <div>
                <p>Now that you have published your study, you need respondents! You can choose to find respondents yourself, or you could use our sourcing services or a custom
                  service to provide respondents for your study.</p>
                <p>Remember, studies made through the BimiLeap platform are FREE. You pay only the per respondent processing fee.
                  If you want your study privatized, there is an additional fee for that. Other costs may be incurred when you require the translation and programming of the standard respondent
                  instruction pages into a new language, as well as handling fees for non-credit billing, or specialized recruiting of respondents.
                </p>
              </div>
            </div>
            <div class="pure-u-1">
              <div class="row-layout">
                <button class="pure-button bigmi-wide-button bigmi-red-2-button" @click="openModal('lucid', true)">
                  <span>I want BimiLeap to provide respondents (general population)</span>
                </button>
                <div class="hint" @click="openModal('lucid', false)">
                  <Fai :icon="['fal', 'question-circle']"/>
                </div>
              </div>
              <div class="margin-top row-layout">
                <button class="pure-button bigmi-wide-button bigmi-red-2-button" @click="openModal('custom', true)">
                  <span>I want a custom sample of respondents from BimiLeap</span>
                </button>
                <div class="hint" @click="openModal('custom', false)">
                  <Fai :icon="['fal', 'question-circle']"/>
                </div>
              </div>
              <div class="margin-top row-layout">
                <button class="pure-button bigmi-wide-button bigmi-red-2-button" @click="openModal('external', true)">
                  <span>I want to use a third-party service or explore more custom sampling options</span>
                </button>
                <div class="hint" @click="openModal('external', false)">
                  <Fai :icon="['fal', 'question-circle']"/>
                </div>
              </div>
              <div class="margin-top row-layout">
                <button class="pure-button bigmi-wide-button bigmi-red-2-button" @click="openModal('self', true)">
                  <span>I will source myself</span>
                </button>
                <div class="hint" @click="openModal('self', false)">
                  <Fai :icon="['fal', 'question-circle']"/>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="showModal">
        <Modal>
          <div class="modal-layout">
            <div class="header">
              <div></div>
              <div class="middle">{{ modalTitle }}</div>
              <div class="right" @click="closeModal">
                <Fai :icon="['fal', 'times']"/>
              </div>
            </div>
            <div class="content">{{ modalContent }}</div>
            <div v-if="ifShowButtonRow" class="button-row">
              <button class="pure-button bigmi-red-2-button each-button" @click="closeModal">
                <span>Cancel</span>
              </button>
              <button class="pure-button bigmi-red-2-button each-button" @click="continueToNextPage">
                <Fai v-if="studyUpdatingLoading" :icon="['far', 'circle-notch']" spin/>
                <span v-else>Continue</span>
              </button>
            </div>
          </div>
        </Modal>
      </div>
    </section>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'

import {
  GET_IS_IN_PROGRESS_STUDY_UPDATING,
  GET_NEXT_STUDY_STEP_ROUTE,
  SET_STUDY_SOURCING_METHOD,
  STUDY_SOURCE_SELECTION_CUSTOM,
  STUDY_SOURCE_SELECTION_EXTERNAL,
  STUDY_SOURCE_SELECTION_LUCID,
  STUDY_SOURCE_SELECTION_SELF,
  UPDATE_STUDY,
} from '@/services/constants'

export default {
  name: 'ChooseSourcingMethod',
  data() {
    return {
      modalOpen: false,
      selection: "",
      showButtons: false
    }
  },
  computed: {
    ...mapGetters([
      GET_NEXT_STUDY_STEP_ROUTE,
      GET_IS_IN_PROGRESS_STUDY_UPDATING,
    ]),
    studyUpdatingLoading() {
      return this[GET_IS_IN_PROGRESS_STUDY_UPDATING]
    },
    showModal() {
      return this.modalOpen === true
    },
    modalTitle() {
      switch (this.selection) {
        case "self":
          return "Self-Sourcing"
        case "lucid":
          return "BimiLeap Sourcing"
        case "custom":
          return "Custom Bimi Sample"
        case "external":
          return "Third-party Sourcing"
        default:
          return ""
      }
    },
    modalContent() {
      switch (this.selection) {
        case "self":
          return this.selfContent
        case "lucid":
          return this.lucidContent
        case "custom":
          return this.customContent
        case "external":
          return this.externalContent
        default:
          return ""
      }
    },
    selfContent() {
      return "This means that you will receive a link for your study to your email which can be shared with anyone you want. Additionally you can send a unique link that can be used once to potential respondents from the study dashboard. After the study is closed and the number of respondents has been fulfilled, you can add additional respondents from the study dashboard. The processing fee for using this method is $2 per respondent. The final pricing will be displayed at the time of payment."
    },
    lucidContent() {
      return "This means that you can order respondents directly from the BimiLeap platform using our sampling service. You will be able to define 7 different parameters (or qualifications) for your respondents: Location, Gender, Age, Education, Income, Martial Status, and Number of Children. The cost is $4 per respondent (within the US and many other countries, however pricing may vary from country to country. The final pricing will be displayed at the time of payment.) Once the order is fulfilled and the study is closed you can request additional respondents from the study dashboard."
    },
    customContent() {
      return "This means that you will work together with a representative from BimiLeap to set up a custom sample for your study. Use this option if the general population qualifications are not enough for your study (e.g., you want respondents from a more specific location or you want respondents that have purchased certain products in the last 30 days, etc. There are many qualifications to choose from, additionally you can define your own custom qualification. Once your study is published you will receive an email from BimiLeap with a unique link for your study and a representative will contact you directly. Pricing for custom sampling will vary between $5 - $10 per respondent, depending on the qualifications requested. An additional setup fee of $500 maybe apply. You will be invoiced for this outside of the platform."
    },
    externalContent() {
      return "This means that you can utilize a third-party sample provider to get respondents for your study. Depending on the nature of the type of sample you need, a BimiLeap representative can help choose the right provider or utilize our existing custom services. Pricing depends on the provider, level of integration necessary for using that provider, and/or the desired custom qualifications for the sample. You will receive any email after your study is published and a representative will contact you directly. You will be invoiced for this outside of the platform."
    },
    ifShowButtonRow() {
      return this.showButtons
    },
    sourcingMethodString() {
      switch (this.selection) {
        case "self":
          return STUDY_SOURCE_SELECTION_SELF
        case "lucid":
          return STUDY_SOURCE_SELECTION_LUCID
        case "custom":
          return STUDY_SOURCE_SELECTION_CUSTOM
        case "external":
          return STUDY_SOURCE_SELECTION_EXTERNAL
        default:
          return ""
      }
    },
  },
  methods: {
    setSourcingMethod(str) {
      this.$store.commit(SET_STUDY_SOURCING_METHOD, str)
    },
    updateStudyAndNavigate() {
      this.$store.dispatch(UPDATE_STUDY).then(() => {
        this.$router.push(this[GET_NEXT_STUDY_STEP_ROUTE])
      })
    },
    openModal(selection, showButtons) {
      this.modalOpen = true;
      this.selection = selection
      this.showButtons = showButtons
    },
    closeModal() {
      this.modalOpen = false;
      this.selection = ""
      this.showButtons = false
    },
    continueToNextPage() {
      this.setSourcingMethod(this.sourcingMethodString)
      this.updateStudyAndNavigate()
    }

  },
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
@import "../../scss/main";

.container {
  height: 100%;
}

section {
  height: 100%;
}

.layout {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100%;
}

.child {
  width: 100%;
  max-width: 500px;
  margin: 1em;
}

h3 {
  color: $cyan-blue-3;
}

.margin-top {
  margin: 2em 0 0 0;
}

.row-layout {
  display: flex;

  button {
    flex-basis: 90%;
    white-space: normal;
  }

  .hint {
    flex-basis: 10%;
    display: flex;
    justify-content: center;
    align-items: center;
    color: $red-2;
  }

  .hint:hover {
    cursor: pointer;
  }
}

.modal-layout {
  background-color: #ffffff;
  padding: 30px;
  border-radius: 3px;

  .header {
    display: grid;
    grid-template-columns: 15% 70% 15%;
    padding: 10px 10px;

    .left {

    }

    .middle {
      justify-self: center;
      color: $red-2;
      font-size: 20px;
      font-weight: 900;
    }

    .right {
      justify-self: right;
      font-size: 20px;
    }

    .right:hover {
      cursor: pointer;
    }
  }

  .content {
    padding: 20px 10px;
    line-height: 1.2;
  }

  .button-row {
    display: flex;
    justify-content: space-around;
    padding-top: 20px;

    .each-button {
      flex-basis: 33%;
    }
  }
}

</style>
